import { Ionicons } from '@expo/vector-icons';
import { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';

import { SuggestionsFlingStack } from '@/components/suggestions/SuggestionsFlingStack';
import { SuggestionHistorySheet } from '@/components/suggestions/SuggestionHistorySheet';
import { SuggestionsLandingState } from '@/components/suggestions/SuggestionsLandingState';
import { CalendarEventModal } from '@/components/calendar/event-modal';
import { deviceCalendarService } from '@/lib/calendar/DeviceCalendarService';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import type { CreateCalendarEventInput, UpdateCalendarEventInput } from '@/types/events';
import type { FamilyMember } from '@/types/family-member';
import type { AISuggestion } from '@/types/ai-suggestions';
import { useAISuggestionsStore } from '@/lib/suggestions/useAISuggestionsStore';
import { AISuggestionsManager } from '@/lib/managers/AISuggestionsManager';
import { SuggestionDescriptionComposer } from '@/lib/suggestions/SuggestionDescriptionComposer';
import { useFamilyMembersStore } from '@/hooks/use-scoped-managers';
import { useResolvedDefaultCalendarId } from '@/hooks/use-resolved-default-calendar-id';
import { useCalendarMemberMapping } from '@/hooks/use-calendar-member-mapping';
import { useSupabaseAuth } from '@/lib/supabase/provider';
import { useChatStore } from '@/lib/chat/useChatStore';
import { contentReportManager } from '@/lib/managers/ContentReportManager';
import { reportContextStore } from '@/lib/report/ReportContextStore';
import { Spacing } from '@/constants/spacing';
import { Colors } from '@/constants/theme';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';

type AISuggestionsModalProps = {
  visible: boolean;
  onClose: () => void;
};

/**
 * AISuggestionsModal - Phase-based overlay for browsing AI suggestions.
 * Landing → closes (background load) → reopens with Results.
 */
export function AISuggestionsModal({ visible, onClose }: AISuggestionsModalProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { session } = useSupabaseAuth();

  const {
    suggestions,
    archivedSuggestions,
    remainingRequests,
    phase,
    errorMessage,
    fetchSuggestions,
    fetchRemainingRequests,
    loadArchivedSuggestions,
    setPhase,
    resetToLanding,
    archiveSuggestion,
  } = useAISuggestionsStore();

  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const [eventSuggestion, setEventSuggestion] = useState<AISuggestion | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [isSubmittingEvent, setIsSubmittingEvent] = useState(false);
  const [writableCalendars, setWritableCalendars] = useState<DeviceCalendar[]>([]);
  const [allCalendars, setAllCalendars] = useState<DeviceCalendar[]>([]);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const { mappings: calendarMappings } = useCalendarMemberMapping();
  const familyMembersStore = useFamilyMembersStore();
  const storedDefaultCalendarId = useResolvedDefaultCalendarId(writableCalendars);

  useEffect(() => {
    if (!visible) return;
    // If there are unactioned suggestions, jump straight to results
    if (phase === 'landing' && suggestions.length > 0) {
      setPhase('results');
    } else if (phase === 'landing') {
      fetchRemainingRequests();
    }
    loadArchivedSuggestions();
    loadCalendarsAndMembers();
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCalendarsAndMembers = useCallback(async () => {
    try {
      const calendars = await deviceCalendarService.getDeviceCalendars();
      setAllCalendars(calendars);
      setWritableCalendars(calendars.filter((cal: DeviceCalendar) => cal.allowsModifications));
    } catch (err) {
      console.error('[AISuggestionsModal] Failed to load calendars:', err);
    }

    if (familyMembersStore) {
      try {
        const members = await familyMembersStore.getAll();
        setFamilyMembers(members);
      } catch (err) {
        console.error('[AISuggestionsModal] Failed to load family members:', err);
      }
    }
  }, [familyMembersStore]);

  const handleAskFim = useCallback(() => {
    fetchSuggestions();
    onClose();
  }, [fetchSuggestions, onClose]);

  const handleAddToCalendar = useCallback((suggestion: AISuggestion) => {
    setEventSuggestion(suggestion);
    setShowEventModal(true);
  }, []);

  const incrementTaskRefreshSignal = useChatStore((state) => state.incrementTaskRefreshSignal);

  const handleAddToTodo = useCallback(async (suggestion: AISuggestion): Promise<boolean> => {
    const userId = session?.user?.id;
    if (!userId) return false;

    const manager = AISuggestionsManager.getInstance();
    const task = await manager.convertToTask(suggestion, userId);
    if (task) {
      archiveSuggestion(suggestion, 'added_to_todo');
      incrementTaskRefreshSignal();
      return true;
    }
    return false;
  }, [session, archiveSuggestion, incrementTaskRefreshSignal]);

  const handleDismiss = useCallback((suggestion: AISuggestion) => {
    archiveSuggestion(suggestion, 'dismissed');
  }, [archiveSuggestion]);

  const handleEventSave = useCallback(async (
    input: (CreateCalendarEventInput | UpdateCalendarEventInput) & { targetCalendarId?: string },
  ) => {
    if (!input.targetCalendarId) return;

    setIsSubmittingEvent(true);
    try {
      await deviceCalendarService.createEvent(input.targetCalendarId, input as CreateCalendarEventInput);
      if (eventSuggestion) {
        archiveSuggestion(eventSuggestion, 'added_to_calendar');
      }
      setShowEventModal(false);
      setEventSuggestion(null);
    } catch (err) {
      console.error('[AISuggestionsModal] Failed to create event:', err);
    } finally {
      setIsSubmittingEvent(false);
    }
  }, [eventSuggestion, archiveSuggestion]);

  const handleEventDismiss = useCallback(() => {
    setShowEventModal(false);
    setEventSuggestion(null);
  }, []);

  const handleClose = useCallback(() => {
    if (phase !== 'background-loading') {
      // Preserve active suggestions — only reset phase so next open re-evaluates
      if (suggestions.length > 0) {
        setPhase('landing');
      } else {
        resetToLanding();
      }
    }
    onClose();
  }, [resetToLanding, setPhase, onClose, phase, suggestions.length]);

  const handleViewHistory = useCallback(() => {
    setIsHistoryVisible(true);
  }, []);

  const handleReport = useCallback(() => {
    reportContextStore.set(contentReportManager.buildSuggestionsContext(suggestions));
    handleClose();
    router.push('/report-ai-content');
  }, [suggestions, handleClose, router]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={handleClose}
      statusBarTranslucent
    >
      <GestureHandlerRootView style={styles.gestureRoot}>
        <View style={styles.overlay}>
          <Animated.View
            entering={FadeIn.duration(200)}
            exiting={FadeOut.duration(150)}
            style={[styles.backdrop, { backgroundColor: palette.overlayScrim }]}
          >
            <Pressable style={StyleSheet.absoluteFill} onPress={handleClose} />
          </Animated.View>

          <ModalHeader
            insetTop={insets.top}
            onHistory={handleViewHistory}
            onClose={handleClose}
            onReport={suggestions.length > 0 ? handleReport : undefined}
          />

          <PhaseContent
            phase={phase}
            suggestions={suggestions}
            remainingRequests={remainingRequests}
            errorMessage={errorMessage}
            onAskFim={handleAskFim}
            onRetry={handleAskFim}
            onAddToCalendar={handleAddToCalendar}
            onAddToTodo={handleAddToTodo}
            onDismiss={handleDismiss}
            onViewHistory={handleViewHistory}
          />
        </View>
      </GestureHandlerRootView>

      <CalendarEventModal
        visible={showEventModal}
        mode="create"
        defaultTitle={eventSuggestion?.title}
        defaultDescription={SuggestionDescriptionComposer.composeEventDescription(
          eventSuggestion?.description,
          eventSuggestion?.actions,
        )}
        preselectedDate={eventSuggestion?.suggestedDate ?? null}
        writableCalendars={writableCalendars}
        allCalendars={allCalendars}
        defaultCalendarId={storedDefaultCalendarId ?? writableCalendars[0]?.id ?? null}
        familyMembers={familyMembers}
        calendarMappings={calendarMappings}
        onSave={handleEventSave}
        onDismiss={handleEventDismiss}
        isSubmitting={isSubmittingEvent}
      />

      <SuggestionHistorySheet
        visible={isHistoryVisible}
        archivedSuggestions={archivedSuggestions}
        onClose={() => setIsHistoryVisible(false)}
        onAddToCalendar={handleAddToCalendar}
        onAddToTodo={handleAddToTodo}
      />
    </Modal>
  );
}

type ModalHeaderProps = {
  insetTop: number;
  onHistory: () => void;
  onClose: () => void;
  onReport?: () => void;
};

function ModalHeader({ insetTop, onHistory, onClose, onReport }: ModalHeaderProps) {
  return (
    <Animated.View
      entering={FadeIn.duration(200).delay(50)}
      style={[styles.header, { paddingTop: insetTop + Spacing.sm }]}
    >
      <View style={styles.headerSpacer} />

      <Text style={[styles.headerTitle, { color: '#FFFFFF', fontFamily: getBodyFont() }]}>
        AI Suggestions
      </Text>

      <View style={styles.headerRight}>
        {onReport && (
          <TouchableOpacity
            onPress={onReport}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityLabel="Report AI suggestions"
          >
            <Ionicons name="flag-outline" size={22} color="#FFFFFF" />
          </TouchableOpacity>
        )}
        <TouchableOpacity
          onPress={onHistory}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Ionicons name="time-outline" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        <TouchableOpacity
          onPress={onClose}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Ionicons name="close" size={28} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
}

type PhaseContentProps = {
  phase: string;
  suggestions: AISuggestion[];
  remainingRequests: number | null;
  errorMessage: string | null;
  onAskFim: () => void;
  onRetry: () => void;
  onAddToCalendar: (suggestion: AISuggestion) => void;
  onAddToTodo: (suggestion: AISuggestion) => Promise<boolean>;
  onDismiss: (suggestion: AISuggestion) => void;
  onViewHistory: () => void;
};

function PhaseContent({
  phase,
  suggestions,
  remainingRequests,
  errorMessage,
  onAskFim,
  onRetry,
  onAddToCalendar,
  onAddToTodo,
  onDismiss,
  onViewHistory,
}: PhaseContentProps) {
  switch (phase) {
    case 'landing':
      return (
        <SuggestionsLandingState
          remainingRequests={remainingRequests}
          onAskFim={onAskFim}
          onViewHistory={onViewHistory}
        />
      );

    case 'results':
      return (
        <SuggestionsFlingStack
          suggestions={suggestions}
          onAddToCalendar={onAddToCalendar}
          onAddToTodo={onAddToTodo}
          onDismiss={onDismiss}
          onViewHistory={onViewHistory}
        />
      );

    case 'error':
      return (
        <Animated.View entering={FadeIn.duration(200)} style={styles.centerContent} pointerEvents="box-none">
          <Ionicons name="warning-outline" size={40} color="#FF6B6B" />
          <Text style={[styles.errorText, { fontFamily: getBodyFont() }]}>
            {errorMessage ?? 'Something went wrong'}
          </Text>
          <TouchableOpacity style={styles.retryButton} onPress={onRetry} activeOpacity={0.8}>
            <Text style={[styles.retryButtonText, { fontFamily: getBodyFont() }]}>
              Try again
            </Text>
          </TouchableOpacity>
        </Animated.View>
      );

    default:
      return null;
  }
}

const styles = StyleSheet.create({
  gestureRoot: {
    flex: 1,
  },
  overlay: {
    flex: 1,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: Spacing.xl,
    paddingBottom: Spacing.md,
  },
  headerSpacer: {
    width: 76,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
  },
  centerContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: Spacing.md,
    paddingHorizontal: Spacing.xxl,
  },
  errorText: {
    fontSize: 15,
    color: 'rgba(255, 255, 255, 0.7)',
    textAlign: 'center',
    lineHeight: 22,
  },
  retryButton: {
    paddingHorizontal: Spacing.xl,
    paddingVertical: Spacing.sm,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
    marginTop: Spacing.sm,
  },
  retryButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
